import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import type { EffectiveAreaResult } from '../../types/apiModels'

interface StrataAccordionHeaderProps {
  strata: {
    properties: {
      id?: string | number
      name: string
      color: string
      area: number
      belongToFarm?: boolean
    }
  }
  canEditSites: boolean
  isViewOnly: boolean
  monitoringSitesBySamplingAreas: Record<string, { properties: Record<string, unknown> }[]>
  effectiveArea?: EffectiveAreaResult
  totalEffectiveArea: number
  hasUnsavedSites?: boolean
  disabledTooltip?: string
  lockReason?: 'zero-area' | 'has-random-sites'
  checkValue: (name: string) => string
  onSitesChange: (strataName: string, value: string) => void
}

const LOCK_REASON_TOOLTIP: Record<'zero-area' | 'has-random-sites', string> = {
  'has-random-sites':
    'Este estrato ya tiene sitios aleatorios. La aleatorización general no lo modificará.',
  'zero-area': 'Sin área efectiva disponible para aleatorizar.',
}

export const StrataAccordionHeader = ({
  strata,
  canEditSites,
  isViewOnly,
  monitoringSitesBySamplingAreas,
  effectiveArea,
  totalEffectiveArea,
  hasUnsavedSites,
  disabledTooltip = '',
  lockReason,
  checkValue,
  onSitesChange,
}: StrataAccordionHeaderProps) => {
  const percentage =
    strata.properties.belongToFarm && effectiveArea && totalEffectiveArea > 0
      ? ((effectiveArea.effectiveArea / totalEffectiveArea) * 100).toFixed(1)
      : null

  // El input se muestra para estratos editables (desbloqueados) sin importar si el plan ya fue
  // guardado: un estrato nuevo agregado tras guardar debe poder recibir su cantidad de sitios.
  const canEnterSites = canEditSites && strata.properties.belongToFarm

  return (
    <Stack
      direction="row"
      spacing={0}
      sx={{
        alignItems: 'stretch',
        width: '100%',
        minHeight: '100%',
      }}
    >
      {/* Vertical Color Band */}
      <Box
        sx={{
          bgcolor: strata.properties.color,
          background: strata.properties.color,
          flexShrink: 0,
          width: 10,
          alignSelf: 'stretch',
          borderRadius: '4px 0 0 4px',
        }}
      />

      {/* Content */}
      <Stack
        alignItems="center"
        direction="row"
        sx={{ flexGrow: 1, py: 1, px: 2, width: '100%', gap: 2 }}
      >
        {/* Nombre del estrato */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Stack direction="column" sx={{ gap: 1 }}>
            <Typography color="text.secondary" variant="caption">
              Nombre del estrato
            </Typography>
            <Typography fontWeight={600} lineHeight={1.2} variant="accordionTitle">
              {strata.properties.name}
            </Typography>
            {strata.properties.belongToFarm && effectiveArea && (
              <Typography color="text.secondary" variant="caption">
                {effectiveArea.totalArea.toFixed(2)} ha
              </Typography>
            )}
          </Stack>
          {hasUnsavedSites && (
            <Box
              data-testid="unsaved-indicator"
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: 'error.main',
                flexShrink: 0,
              }}
            />
          )}
          {!canEditSites && lockReason && (
            <Tooltip arrow placement="top" title={LOCK_REASON_TOOLTIP[lockReason]}>
              <LockOutlinedIcon
                data-testid="lock-icon"
                sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }}
              />
            </Tooltip>
          )}
        </Box>

        {/* Área a muestrear */}
        {strata.properties.belongToFarm && effectiveArea && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography color="text.secondary" variant="caption">
              Área a muestrear
            </Typography>
            <Typography fontWeight={600} lineHeight={1.2} variant="accordionTitle">
              {percentage ? `${percentage}%` : '0.0%'}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {effectiveArea.effectiveArea.toFixed(2)} ha
            </Typography>
          </Box>
        )}

        {/* Cantidad de sitios */}
        <Box sx={{ minWidth: '150px', flexShrink: 0 }}>
          {canEnterSites ? (
            <Tooltip arrow title={disabledTooltip}>
              <span>
                <TextField
                  required
                  autoComplete="off"
                  disabled={isViewOnly}
                  error={Boolean(checkValue(strata.properties.name))}
                  helperText={checkValue(strata.properties.name)}
                  label="N° sitios aleatorios"
                  placeholder="ej. 5"
                  size="small"
                  slotProps={{
                    htmlInput: { min: 3, max: 30, step: 1 },
                  }}
                  sx={{ width: '100%' }}
                  type="number"
                  onChange={e => {
                    onSitesChange(strata.properties.name, e.target.value)
                  }}
                  onClick={e => {
                    e.stopPropagation()
                  }}
                />
              </span>
            </Tooltip>
          ) : (
            <Typography fontWeight={600} variant="accordionTitle">
              {monitoringSitesBySamplingAreas[strata.properties.name]?.length || 0} sitios
            </Typography>
          )}
        </Box>
      </Stack>
    </Stack>
  )
}
